import { useState } from 'react'
import { useTexte } from '../../texte'

import { useSaveSettings, useSettings } from '../../hooks/queries'
import type { Zahlenwert } from '../../api/types'
import { fehlertext } from '../../api/client'

/**
 * Bildgröße und Grenzen -- einstellbar ohne FTP.
 *
 * Bisher standen diese Zahlen in der `.env` und die Bildgröße sogar fest in
 * der ausgelieferten Releaseversion: eine Änderung hieß neu bauen, hochladen, hoffen.
 * Unterwegs, mit dem Telefon in der Hand, ist das kein Weg.
 *
 * **Die Karte zeigt sich nur der Verwaltung** -- und zwar, weil der Server
 * die Decke mitschickt, nicht weil die Oberfläche aus der Rolle schließt.
 * Wer sie nicht bekommt, sieht nichts; wer sie an den Server vorbei aufruft,
 * bekommt vom Server ein Nein.
 *
 * **Ein Wert hier ist keine Zahl** (`video_latency`), und der Entwurf oben
 * hält nur Zahlen -- aus gutem Grund, er rechnet sie am Ende um. Diese eine
 * Auswahl geht deshalb **sofort** hinaus, so wie die Kartenquelle, und lässt
 * den Entwurf in Ruhe. Sie steht hier und nicht in einer eigenen Karte, weil
 * sie zu den Videogrenzen darüber gehört.
 */
export function LimitsCard() {
  const t = useTexte()
  const settings = useSettings()
  const save = useSaveSettings()
  // Nur die Zahlen: die Farbe der Route hat eine eigene Karte, und ein
  // gemeinsamer Entwurf müsste beide Arten von Wert aushalten.
  const [entwurf, setEntwurf] = useState<Partial<Record<Zahlenwert, string>>>({})
  const [gemeldet, setGemeldet] = useState(false)

  const daten = settings.data
  // Ohne Decke ist dieses Konto keine Verwaltung -- der Server hat entschieden.
  if (!daten?.ceiling || !daten.limits) return null

  const werte = daten.settings
  const decke = daten.ceiling
  const grenzen = daten.limits

  function wert(name: Zahlenwert): string {
    return entwurf[name] ?? String(werte[name])
  }

  function geaendert(): Partial<Record<Zahlenwert, number>> {
    const teil: Partial<Record<Zahlenwert, number>> = {}
    for (const [name, roh] of Object.entries(entwurf)) {
      const zahl = Number(roh)
      if (!Number.isInteger(zahl)) continue
      if (zahl === werte[name as Zahlenwert]) continue
      teil[name as Zahlenwert] = zahl
    }
    return teil
  }

  const offen = Object.keys(geaendert()).length > 0

  function speichern() {
    setGemeldet(false)
    save.mutate(geaendert(), {
      onSuccess: () => {
        setEntwurf({})
        setGemeldet(true)
      },
    })
  }

  return (
    <div className="card card--seite">
      <h2 className="card__titel">{t.grenzen.titel}</h2>
      <p className="card__lead">{t.grenzen.lead}</p>

      <Feld
        name="image_max_edge"
        label={t.grenzen.fotoKante}
        einheit={t.grenzen.pixel}
        hinweis={t.grenzen.fotoKanteHinweis}
        wert={wert('image_max_edge')}
        decke={decke.image_max_edge}
        grenzen={grenzen.image_max_edge}
        onChange={(v) => setEntwurf({ ...entwurf, image_max_edge: v })}
      />
      <Feld
        name="image_quality"
        label={t.grenzen.fotoQualitaet}
        einheit="%"
        hinweis={t.grenzen.fotoQualitaetHinweis}
        wert={wert('image_quality')}
        decke={decke.image_quality}
        grenzen={grenzen.image_quality}
        onChange={(v) => setEntwurf({ ...entwurf, image_quality: v })}
      />
      {/* **Dieselben zwei Zahlen für Mitlesende** (#59). Sie stehen unmittelbar
          unter denen des Autors, weil man sie im Verhältnis dazu einstellt:
          der Bericht ist das, was bleibt und gedruckt wird, eine Antwort ist
          eine Antwort. Eingestellt wird es einmal und gilt für alle. */}
      <Feld
        name="reader_image_max_edge"
        label={t.grenzen.leserKante}
        einheit={t.grenzen.pixel}
        hinweis={t.grenzen.leserKanteHinweis}
        wert={wert('reader_image_max_edge')}
        decke={decke.reader_image_max_edge}
        grenzen={grenzen.reader_image_max_edge}
        onChange={(v) => setEntwurf({ ...entwurf, reader_image_max_edge: v })}
      />
      <Feld
        name="reader_image_quality"
        label={t.grenzen.leserQualitaet}
        einheit="%"
        wert={wert('reader_image_quality')}
        decke={decke.reader_image_quality}
        grenzen={grenzen.reader_image_quality}
        onChange={(v) => setEntwurf({ ...entwurf, reader_image_quality: v })}
      />
      {/* The thumbnail's edge (1.4.0): it was a constant of 400 and looked
          grainy on the iPad, where a tile at double density needs 640 or
          more. Never above the photo's edge -- the server caps it. */}
      <Feld
        name="thumb_max_edge"
        label={t.grenzen.vorschauKante}
        einheit={t.grenzen.pixel}
        hinweis={t.grenzen.vorschauKanteHinweis}
        wert={wert('thumb_max_edge')}
        decke={decke.thumb_max_edge}
        grenzen={grenzen.thumb_max_edge}
        onChange={(v) => setEntwurf({ ...entwurf, thumb_max_edge: v })}
      />
      {/* How long a voice recording may run (1.9.0). It was a constant of
          two minutes in the browser; no ceiling from the `.env`, because
          the server caps the bytes, not the seconds. */}
      <Feld
        name="voice_max_seconds"
        label={t.grenzen.aufnahmeDauer}
        einheit={t.grenzen.sekunden}
        hinweis={t.grenzen.aufnahmeDauerHinweis}
        wert={wert('voice_max_seconds')}
        grenzen={grenzen.voice_max_seconds}
        onChange={(v) => setEntwurf({ ...entwurf, voice_max_seconds: v })}
      />
      {/* How many other pictures "Aus dieser Reise" offers (1.53.3). What
          matches the point is never capped; the number is for the rest. No
          ceiling from the `.env`: the server counts rows, not bytes. */}
      <Feld
        name="picker_max_media"
        label={t.grenzen.auswahlMax}
        einheit={t.grenzen.bilder}
        hinweis={t.grenzen.auswahlMaxHinweis}
        wert={wert('picker_max_media')}
        grenzen={grenzen.picker_max_media}
        onChange={(v) => setEntwurf({ ...entwurf, picker_max_media: v })}
      />
      <Feld
        name="image_max_mb"
        label={t.grenzen.fotoMax}
        einheit="MB"
        wert={wert('image_max_mb')}
        decke={decke.image_max_mb}
        grenzen={grenzen.image_max_mb}
        onChange={(v) => setEntwurf({ ...entwurf, image_max_mb: v })}
      />
      <Feld
        name="video_max_edge"
        label={t.grenzen.videoKante}
        einheit={t.grenzen.pixel}
        hinweis={t.grenzen.videoKanteHinweis}
        wert={wert('video_max_edge')}
        decke={decke.video_max_edge}
        grenzen={grenzen.video_max_edge}
        onChange={(v) => setEntwurf({ ...entwurf, video_max_edge: v })}
      />
      <Feld
        name="video_max_fps"
        label={t.grenzen.videoFps}
        einheit="fps"
        hinweis={t.grenzen.videoFpsHinweis}
        wert={wert('video_max_fps')}
        decke={decke.video_max_fps}
        grenzen={grenzen.video_max_fps}
        onChange={(v) => setEntwurf({ ...entwurf, video_max_fps: v })}
      />
      <Feld
        name="video_max_mb"
        label={t.grenzen.videoMax}
        einheit="MB"
        hinweis={t.grenzen.videoMaxHinweis}
        wert={wert('video_max_mb')}
        decke={decke.video_max_mb}
        grenzen={grenzen.video_max_mb}
        onChange={(v) => setEntwurf({ ...entwurf, video_max_mb: v })}
      />
      {/* **Der eine Wert hier, der keine Zahl ist** -- und deshalb an den
          Zahlen-Entwurf oben vorbei: er geht sofort hinaus, so wie die
          Kartenquelle. Er steht trotzdem hier und nicht in einer eigenen
          Karte, weil er zu den drei Videogrenzen darüber gehört; wer die
          einstellt, stellt auch das ein. */}
      <label>
        <span>{t.grenzen.rechnen}</span>
        <select
          name="video_latency"
          value={werte.video_latency}
          onChange={(event) => {
            setGemeldet(false)
            save.mutate({ video_latency: event.target.value })
          }}
        >
          <option value="quality">{t.grenzen.rechnenQualitaet}</option>
          <option value="realtime">{t.grenzen.rechnenTempo}</option>
        </select>
        {/* **Ein Satz, nicht acht.** Hier stand die ganze Beweisführung —
            Gerät, Filmlänge, zwei Zahlenpaare, ein Vorbehalt zu ungemessenen
            Geräten. Aus dem Betrieb: „diese ausführliche Erläuterung ist
            nicht erforderlich." Die Messung steht weiter da, wo sie
            hingehört: am Doc-Block von `video_latency` in `Settings.php`. */}
        <span className="feld__hinweis">{t.grenzen.rechnenHinweis}</span>
      </label>
      <Feld
        name="trip_quota_mb"
        label={t.grenzen.platzJeReise}
        einheit="MB"
        wert={wert('trip_quota_mb')}
        decke={decke.trip_quota_mb}
        grenzen={grenzen.trip_quota_mb}
        onChange={(v) => setEntwurf({ ...entwurf, trip_quota_mb: v })}
      />

      {/* Warum ein Wert wieder kleiner dasteht, als man ihn eingetragen hat.
          Ohne diesen Satz hielte man das für einen Fehler. */}
      <p className="feld__hinweis">{t.grenzen.deckel}</p>

      {save.isError && (
        <p className="notice" role="alert">
          {fehlertext(save.error)}
        </p>
      )}
      {gemeldet && !save.isPending && (
        <p className="card__lead akzent">{t.allgemein.gespeichert}</p>
      )}

      <button
        className="button button--outline"
        type="button"
        disabled={!offen || save.isPending}
        onClick={speichern}
      >
        {save.isPending ? t.allgemein.laeuft : t.allgemein.speichern}
      </button>
    </div>
  )
}

function Feld({
  name,
  label,
  einheit,
  hinweis,
  wert,
  decke,
  grenzen,
  onChange,
}: {
  name: string
  label: string
  einheit: string
  hinweis?: string
  wert: string
  /** Die Decke aus der `.env`; fehlt sie, gilt allein der Bereich (1.9.0, Aufnahmedauer). */
  decke?: number
  grenzen: { min: number; max: number }
  onChange: (wert: string) => void
}) {
  const t = useTexte()

  return (
    <label className="field field--luft">
      <span className="field__label">{t.grenzen.feld(label, einheit)}</span>
      <input
        className="field__input"
        type="number"
        inputMode="numeric"
        name={name}
        value={wert}
        min={grenzen.min}
        max={decke === undefined ? grenzen.max : Math.min(grenzen.max, decke)}
        onChange={(event) => onChange(event.target.value)}
      />
      <span className="feld__hinweis">
        {hinweis ? `${hinweis} ` : ''}
        {decke === undefined ? t.grenzen.bereich(grenzen.min, grenzen.max) : t.grenzen.hoechstens(decke)}
      </span>
    </label>
  )
}
